import React, { useState, useContext, useEffect, useMemo } from "react";
import axios from "axios";
import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";
import HeaderBar from "../../components/HeaderBar";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaSave } from "react-icons/fa";
import SalesLayoutWrapper from "../../components/SalesLayoutWrapper";
import { useNavigate, useLocation } from "react-router-dom";

const BASE_URL = import.meta?.env?.VITE_BASE_URL || "https://nissi-india.com";

// convert any value to safe numeric-string for <input type="number" />
function toNumberText(v) {
  const s = String(v ?? "").trim();
  const m = s.match(/-?\d+(\.\d+)?/);
  return m ? m[0] : "";
}

// safe yyyy-mm-dd for <input type="date" />
function toDateText(v) {
  if (!v) return "";
  try {
    const s = String(v);
    if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
    const d = new Date(v);
    if (isNaN(d.getTime())) return "";
    return d.toISOString().slice(0, 10);
  } catch {
    return "";
  }
}

const FullPaymentEntry = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const navigate = useNavigate();
  const location = useLocation();
  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);

  // edit support
  const editCustId = query.get("id") || query.get("custid") || query.get("customerId") || "";
  const isEdit = !!editCustId;

  // localStorage values
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId = localStorage.getItem("branchId") || "";
  const branch = localStorage.getItem("branchType") || "";
  const userId = localStorage.getItem("userId") || "";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branch, userId]
  );

  // -------------------------
  // Form state
  // -------------------------
  const [formData, setFormData] = useState({
    customerId: "",
    customerName: "",
    leadSource: "",
    productName: "",
    salesPerson: "",
    fullAddress: "",
    mobileNumber: "",
    totalSqFeet: "",
    plotNoAssetCode: "",
    assetClass: "",
    location: "",
    address: "",
    propertyInNameOf: "",
    dateOfAcquisition: "",
    valueOfAsset: "",
    billedAmount: "",
    date: "",
    paymentMode: "Cash",
    remarks: "",
    payingAmount: "",
    balanceAmount: "",
    enquiryNo: "",
  });

  const [customerIds, setCustomerIds] = useState([]);
  const [plotNumbers, setPlotNumbers] = useState([]);
  const [loading, setLoading] = useState(false);

  const api = {
    customers: `${BASE_URL}/api/fullpaymentEntry/customers`,
    assetCodes: `${BASE_URL}/api/fullpaymentEntry/assetCodes`,
    assetDetails: `${BASE_URL}/api/fullpaymentEntry/customerAssetDetails`,
    edit: `${BASE_URL}/api/fullpaymentEntry/edit`,
    insert: `${BASE_URL}/api/fullpaymentEntry/insert`,
    markPaid: `${BASE_URL}/api/fullpaymentEntry/markPaid`,
    billno: `${BASE_URL}/api/fullpaymentEntry/billno`,
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    if (field === "billedAmount" || field === "payingAmount") {
      const billed =
        field === "billedAmount" ? parseFloat(value) || 0 : parseFloat(prevSafe(formData.billedAmount)) || 0;
      const paying =
        field === "payingAmount" ? parseFloat(value) || 0 : parseFloat(prevSafe(formData.payingAmount)) || 0;

      const balance = billed - paying;
      setFormData((prev) => ({ ...prev, [field]: value, balanceAmount: String(balance) }));
    }
  };

  function prevSafe(v) {
    const t = toNumberText(v);
    return t === "" ? "0" : t;
  }

  const validateForm = () => {
    const requiredFields = ["customerId", "customerName", "mobileNumber", "billedAmount", "date", "payingAmount"];

    for (let field of requiredFields) {
      if (!formData[field] || String(formData[field]).trim() === "") {
        toast.error(`Please fill in ${field.replace(/([A-Z])/g, " $1").toLowerCase()}`);
        return false;
      }
    }

    const mobileRegex = /^[6-9]\d{9}$/;
    if (!mobileRegex.test(String(formData.mobileNumber))) {
      toast.error("Please enter a valid mobile number");
      return false;
    }

    const billedAmount = parseFloat(toNumberText(formData.billedAmount) || "0");
    const payingAmount = parseFloat(toNumberText(formData.payingAmount) || "0");

    if (billedAmount <= 0) {
      toast.error("Billed amount must be greater than 0");
      return false;
    }

    if (payingAmount <= 0) {
      toast.error("Paying amount must be greater than 0");
      return false;
    }

    if (payingAmount !== billedAmount) {
      toast.error("Paying amount must equal billed amount for full payment");
      return false;
    }

    if (!formData.enquiryNo) {
      toast.error("Enquiry No not found. Please select customer again.");
      return false;
    }

    return true;
  };

  // -------------------------
  // API Calls
  // -------------------------
  const loadCustomers = async () => {
    const res = await axios.get(api.customers, {
      params: { type: "Full Payment" },
      headers: commonHeaders,
    });
    setCustomerIds(res.data?.data || []);
  };

  const loadAssetCodes = async () => {
    const res = await axios.get(api.assetCodes, {
      headers: commonHeaders,
    });
    setPlotNumbers(res.data?.data || []);
  };

  const loadCustomerAssetDetails = async (customerId) => {
    if (!customerId) return;

    const res = await axios.get(api.assetDetails, {
      params: { customerId },
      headers: commonHeaders,
    });

    const d = res.data?.data;
    if (!d) return;

    const purchase = toNumberText(d.fld_PurchaseValue ?? d.fld_purchasevalue ?? "");
    const billedDefault = purchase;
    const payingDefault = purchase;
    const balDefault = String((Number(purchase || 0) - Number(purchase || 0)) || 0);

    setFormData((prev) => ({
      ...prev,
      customerId: d.fld_customerid ?? prev.customerId,
      customerName: d.fld_customername ?? prev.customerName,
      enquiryNo: d.fld_enquirnum ?? d.fld_enquiryno ?? prev.enquiryNo,
      leadSource: d.fld_leadsource ?? prev.leadSource,
      productName: d.fld_Productname ?? d.fld_productname ?? prev.productName,
      salesPerson: d.fld_salesperson ?? prev.salesPerson,
      fullAddress: d.fld_fulladdress ?? prev.fullAddress,
      mobileNumber: d.fld_mobile ?? d.fld_mobileno ?? prev.mobileNumber,
      totalSqFeet: toNumberText(d.fld_extend ?? d.fld_totalsqfeet ?? prev.totalSqFeet),
      plotNoAssetCode: d.fld_assetid ?? prev.plotNoAssetCode,
      assetClass: d.fld_assetclass ?? prev.assetClass,
      location: d.fld_location ?? prev.location,
      address: d.fld_address ?? prev.address,
      propertyInNameOf: d.fld_propertyname ?? prev.propertyInNameOf,
      dateOfAcquisition: d.fld_acquisitiondate ? toDateText(d.fld_acquisitiondate) : prev.dateOfAcquisition,
      valueOfAsset: toNumberText(d.fld_PurchaseValue ?? d.fld_purchasevalue ?? prev.valueOfAsset),
      billedAmount: billedDefault || prev.billedAmount,
      payingAmount: payingDefault || prev.payingAmount,
      balanceAmount: balDefault,
    }));
  };

  const loadEditData = async (custid) => {
    const res = await axios.get(api.edit, {
      params: { custid },
      headers: commonHeaders,
    });

    const row = Array.isArray(res.data?.data) ? res.data.data[0] : res.data?.data;
    if (!row) return;

    const billed = toNumberText(row.fld_assetvalue ?? row.fld_assetValue ?? "");
    const paying = toNumberText(row.fld_payingamount ?? "");
    const balance = String((Number(billed || 0) - Number(paying || 0)) || 0);

    setFormData((prev) => ({
      ...prev,
      customerId: row.fld_customerid ?? prev.customerId,
      customerName: row.fld_customername ?? prev.customerName,
      enquiryNo: row.fld_enquirnum ?? row.fld_enquiryno ?? prev.enquiryNo,
      leadSource: row.fld_leadsource ?? prev.leadSource,
      productName: row.fld_Productname ?? row.fld_productname ?? prev.productName,
      salesPerson: row.fld_salesperson ?? prev.salesPerson,
      fullAddress: row.fld_fulladdress ?? prev.fullAddress,
      mobileNumber: row.fld_mobile ?? row.fld_mobileno ?? prev.mobileNumber,
      totalSqFeet: toNumberText(row.fld_extend ?? row.fld_totalsqfeet ?? prev.totalSqFeet),
      plotNoAssetCode: row.fld_assetid ?? prev.plotNoAssetCode,
      assetClass: row.fld_assetclass ?? prev.assetClass,
      location: row.fld_location ?? prev.location,
      address: row.fld_address ?? prev.address,
      propertyInNameOf: row.fld_propertyname ?? prev.propertyInNameOf,
      dateOfAcquisition: row.fld_acquisitiondate ? toDateText(row.fld_acquisitiondate) : prev.dateOfAcquisition,
      valueOfAsset: toNumberText(row.fld_PurchaseValue ?? row.fld_purchasevalue ?? prev.valueOfAsset),
      billedAmount: billed || prev.billedAmount,
      payingAmount: paying || prev.payingAmount,
      balanceAmount: balance,
      date: row.fld_date ? toDateText(row.fld_date) : prev.date,
      paymentMode: row.fld_amounttype ?? prev.paymentMode,
      remarks: row.fld_remarks ?? prev.remarks,
    }));
  };

  useEffect(() => {
    (async () => {
      try {
        await Promise.all([loadCustomers(), loadAssetCodes()]);

        if (isEdit) {
          setFormData((p) => ({ ...p, customerId: editCustId }));
          await loadCustomerAssetDetails(editCustId);
          await loadEditData(editCustId);
        }
      } catch (err) {
        console.error(err);
        toast.error("Failed to load entry data");
      }
    })();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!isEdit && formData.customerId) {
      loadCustomerAssetDetails(formData.customerId);
    }
    // eslint-disable-next-line
  }, [formData.customerId, isEdit]);

const getNextBillNo = async () => {
  const res = await axios.get(api.billno, {
    headers: commonHeaders,
  });

  const lastBillNo = Number(res.data?.data?.fld_billno ?? 0);

  if (!Number.isFinite(lastBillNo)) {
    throw new Error("Invalid bill number received from server");
  }

  return lastBillNo + 1;
};

  const handleSave = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      // Get the next unique bill number before inserting the payment.
      const nextBillNo = await getNextBillNo();

      const payload = {
        customerid: formData.customerId,
        fld_assetid: formData.plotNoAssetCode,
        fld_enquiryno: formData.enquiryNo,
        fld_location: formData.location || "",
        fld_paymode: "FULL",
        fld_assetvalue: Number(toNumberText(formData.billedAmount) || 0),
        fld_payingamount: Number(toNumberText(formData.payingAmount) || 0),
        fld_date: formData.date,
        fld_billno: String(nextBillNo),
        fld_amounttype: formData.paymentMode,
        fld_createddate: new Date().toISOString(),
        fld_createdby: userId || "0",
        fld_ipaddress: "0.0.0.0",
        remarks: formData.remarks || "",
        balamount: Number(toNumberText(formData.balanceAmount) || 0),
        nextduedate: null,
      };

      console.log("FULL PAYMENT PAYLOAD:", payload);

      await axios.post(api.insert, payload, {
        headers: commonHeaders,
      });

      await axios.put(
        api.markPaid,
        { customerId: formData.customerId },
        {
          headers: commonHeaders,
        }
      );

      toast.success("Full payment entry saved successfully!");
      navigate("/Sales/FullPaymentListing");
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Save failed");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFormData({
      customerId: "",
      customerName: "",
      leadSource: "",
      productName: "",
      salesPerson: "",
      fullAddress: "",
      mobileNumber: "",
      totalSqFeet: "",
      plotNoAssetCode: "",
      assetClass: "",
      location: "",
      address: "",
      propertyInNameOf: "",
      dateOfAcquisition: "",
      valueOfAsset: "",
      billedAmount: "",
      date: "",
      paymentMode: "Cash",
      remarks: "",
      payingAmount: "",
      balanceAmount: "",
      enquiryNo: "",
    });
    toast.info("Form reset successfully");
  };

  const amountsDisabled = !isEdit && !!formData.customerId;

  return (
    <SalesLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">FULL PAYMENT ENTRY</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex justify-between items-center mb-6">
            <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
              Full Payment Entry
            </h1>
            <div className="flex items-center gap-4">
              <button
                onClick={handleReset}
                className={`px-6 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                Reset
              </button>
            </div>
          </div>

          <div className={`p-6 rounded-lg mb-6 ${isDark ? "bg-gray-800/50" : "bg-blue-50"}`}>
            <h3 className={`text-lg font-semibold mb-4 ${isDark ? "text-white" : "text-blue-900"}`}>
              Customer Information
            </h3>

            {formData.enquiryNo ? (
              <div className={`mb-4 text-sm ${isDark ? "text-gray-200" : "text-gray-700"}`}>
                <span className="font-semibold">Enquiry No:</span> {formData.enquiryNo}
              </div>
            ) : null}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Customer ID <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.customerId}
                    disabled={isEdit}
                    onChange={(e) => {
                      const cid = e.target.value;
                      handleInputChange("customerId", cid);

                      const found = customerIds.find((x) => String(x.fld_customerid) === String(cid));
                      if (found) handleInputChange("customerName", found.fld_customername);
                    }}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                    }`}
                  >
                    <option value="">Select</option>
                    {customerIds.map((x) => (
                      <option key={x.fld_customerid} value={x.fld_customerid}>
                        {x.fld_customerid}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Customer Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.customerName}
                    onChange={(e) => handleInputChange("customerName", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Lead Source
                  </label>
                  <input
                    type="text"
                    value={formData.leadSource}
                    onChange={(e) => handleInputChange("leadSource", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Product Name
                  </label>
                  <input
                    type="text"
                    value={formData.productName}
                    onChange={(e) => handleInputChange("productName", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Sales Person
                  </label>
                  <input
                    type="text"
                    value={formData.salesPerson}
                    onChange={(e) => handleInputChange("salesPerson", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Full Address
                  </label>
                  <textarea
                    rows="3"
                    value={formData.fullAddress}
                    onChange={(e) => handleInputChange("fullAddress", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Mobile Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    value={formData.mobileNumber}
                    onChange={(e) => handleInputChange("mobileNumber", e.target.value)}
                    maxLength="10"
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Total Sq.Feet
                  </label>
                  <input
                    type="number"
                    value={formData.totalSqFeet}
                    onChange={(e) => handleInputChange("totalSqFeet", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }`}
                  />
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Plot No / Asset Code
                  </label>
                  <select
                    value={formData.plotNoAssetCode}
                    onChange={(e) => handleInputChange("plotNoAssetCode", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                    }`}
                  >
                    <option value="">Select</option>
                    {plotNumbers.map((x) => (
                      <option key={x.fld_assetid} value={x.fld_assetid}>
                        {x.fld_assetcode}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Asset Class
                  </label>
                  <input
                    type="text"
                    value={formData.assetClass}
                    onChange={(e) => handleInputChange("assetClass", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Location
                  </label>
                  <input
                    type="text"
                    value={formData.location}
                    onChange={(e) => handleInputChange("location", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Address
                  </label>
                  <input
                    type="text"
                    value={formData.address}
                    onChange={(e) => handleInputChange("address", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Property in the Name of
                  </label>
                  <textarea
                    rows="3"
                    value={formData.propertyInNameOf}
                    onChange={(e) => handleInputChange("propertyInNameOf", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Date of Acquisition
                  </label>
                  <input
                    type="date"
                    value={formData.dateOfAcquisition}
                    onChange={(e) => handleInputChange("dateOfAcquisition", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                    }`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Value of Asset
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.valueOfAsset}
                    onChange={(e) => handleInputChange("valueOfAsset", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }`}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className={`p-6 rounded-lg mb-6 ${isDark ? "bg-gray-800/50" : "bg-purple-50"}`}>
            <h3 className={`text-lg font-semibold mb-4 ${isDark ? "text-white" : "text-purple-900"}`}>
              Payment Details
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Billed Amount <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.billedAmount}
                    disabled={amountsDisabled || isEdit}
                    onChange={(e) => handleInputChange("billedAmount", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={formData.date}
                    onChange={(e) => handleInputChange("date", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                    }`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Payment Mode
                  </label>
                  <div className="flex gap-4">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="paymentMode"
                        value="Cash"
                        checked={formData.paymentMode === "Cash"}
                        onChange={(e) => handleInputChange("paymentMode", e.target.value)}
                        className="mr-2"
                      />
                      <span className={isDark ? "text-gray-300" : "text-gray-700"}>Cash</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="paymentMode"
                        value="Bank"
                        checked={formData.paymentMode === "Bank"}
                        onChange={(e) => handleInputChange("paymentMode", e.target.value)}
                        className="mr-2"
                      />
                      <span className={isDark ? "text-gray-300" : "text-gray-700"}>Bank</span>
                    </label>
                  </div>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Remarks
                  </label>
                  <textarea
                    rows="3"
                    value={formData.remarks}
                    onChange={(e) => handleInputChange("remarks", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }`}
                  />
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Paying Amount <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.payingAmount}
                    disabled={amountsDisabled || isEdit}
                    onChange={(e) => handleInputChange("payingAmount", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Balance Amount
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.balanceAmount}
                    readOnly
                    className={`w-full px-4 py-2 border rounded-lg bg-gray-100 ${
                      isDark ? "bg-gray-600 border-gray-600 text-gray-300" : "bg-gray-100 border-gray-300 text-gray-600"
                    }`}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-center">
            <button
              onClick={handleSave}
              disabled={loading || isEdit}
              className={`flex items-center gap-2 px-8 py-3 rounded-lg font-semibold text-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                isDark ? `${buttonClass} ${hoverClass} text-white` : `${buttonClass} ${hoverClass} text-white`
              } ${(loading || isEdit) ? "opacity-60 cursor-not-allowed" : ""}`}
            >
              <FaSave />
              {isEdit ? "View Only" : loading ? "Saving..." : "Save"}
            </button>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </SalesLayoutWrapper>
  );
};

export default FullPaymentEntry;